import { produce } from 'immer';
import { ItemTypes } from '../constants/constants';
import { mutateRemoveContainers } from './containers';
import { mutateRemoveBox } from './boxes';
import { isInLinkedSectionAnchors } from './linkedSectionAnswers';

/*
 * Zones des sections liées d'un document. À la création depuis un modèle, chaque
 * section liée est injectée puis son ancre (container dédié portant la box
 * SECTION_LINK) est retirée. Pour pouvoir recharger la section plus tard, chaque
 * container de la section porte `linkedSection` : l'id de l'ancre, la section,
 * et une copie de l'ancre à réinsérer.
 *
 * Le marqueur est posé sur le container de l'ancre avant l'injection, puis
 * hérité par tout container injecté à côté d'une box de la zone
 * (mutateInjectSection) : sous-sections liées, sections des questions,
 * sections récursives dépliées au chargement. La zone va du premier au dernier
 * container marqué, blocs ajoutés à la main compris : recharger remplace tout.
 */

const isTopLevelAnchor = (box) =>
  box.type === ItemTypes.SECTION_LINK && box.sectionId && !box.fromBoxId;

/** Copie des ancres de 1er niveau d'un contenu de modèle, avant injection. */
export const snapshotLinkedSectionAnchors = (content) =>
  (content.boxes ?? [])
    .filter(isTopLevelAnchor)
    .map((box) => {
      const container = content.containers.find((c) =>
        c.columnsIds.includes(box.columnId)
      );
      if (!container) return null;
      return {
        anchorId: box.id,
        sectionId: box.sectionId,
        container,
        columns: content.columns.filter((c) =>
          container.columnsIds.includes(c.id)
        ),
        boxes: content.boxes.filter((b) =>
          container.columnsIds.includes(b.columnId)
        ),
      };
    })
    .filter(Boolean);

/** Marque le container de chaque ancre, avant son injection. */
export const markLinkedSectionAnchors = (containers, anchors) => {
  if (!anchors.length) return containers;
  return containers.map((container) => {
    const anchor = anchors.find((a) => a.container.id === container.id);
    return anchor ? { ...container, linkedSection: anchor } : container;
  });
};

/**
 * Zones à recharger, dans l'ordre du document. Deux zones qui se chevauchent
 * (containers déplacés à la main) sont fusionnées : leurs ancres sont
 * réinsérées ensemble, dans leur ordre d'apparition.
 */
export const groupLinkedSectionZones = (containers = []) => {
  const zones = new Map();
  containers.forEach((container, index) => {
    const anchor = container.linkedSection;
    if (!anchor) return;
    const zone = zones.get(anchor.anchorId);
    if (zone) zone.end = index;
    else zones.set(anchor.anchorId, { anchor, start: index, end: index });
  });
  const groups = [];
  [...zones.values()]
    .sort((a, b) => a.start - b.start)
    .forEach((zone) => {
      const last = groups[groups.length - 1];
      if (last && zone.start <= last.end) {
        last.end = Math.max(last.end, zone.end);
        last.anchors.push(zone.anchor);
      } else {
        groups.push({ start: zone.start, end: zone.end, anchors: [zone.anchor] });
      }
    });
  return groups;
};

export const hasLinkedSectionZones = (containers = []) =>
  containers.some((c) => c.linkedSection);

/**
 * Retire chaque zone et remet ses ancres à sa place : le contenu est alors prêt
 * pour la même injection qu'à la création (expandLinkedSectionsAtCreation).
 */
export const restoreLinkedSectionAnchors = (content, groups, currentDocument) =>
  produce(content, (draft) => {
    // Lu par la suppression des box (nettoyage de leurs triggers).
    draft.currentDocument = currentDocument;
    // Calculé avant toute suppression. La suppression d'un container emporte
    // aussi son container lié (séparateur de chapitre et son titre), parfois
    // hors de la zone : l'ancre se place devant le premier container qui
    // reste après la zone, pas à un index devenu faux.
    const plans = groups.map(({ start, end, anchors }) => {
      const zone = draft.containers.slice(start, end + 1);
      const removed = new Set(zone.map((c) => c.id));
      zone.forEach((c) => c.linkedContainerId && removed.add(c.linkedContainerId));
      const next = draft.containers.slice(end + 1).find((c) => !removed.has(c.id));
      return { ids: zone.map((c) => c.id), nextId: next?.id, anchors };
    });
    plans.forEach(({ ids, nextId, anchors }) => {
      mutateRemoveContainers(draft, ids);
      // Les questions de formulaire (onlyForm) n'ont pas de colonne : retirer
      // les containers les laissait en double, orphelines, à côté des neuves.
      const anchorIds = anchors.map(({ anchorId }) => anchorId);
      draft.boxes
        .filter((b) => !b.columnId && isInLinkedSectionAnchors(b, anchorIds))
        .map(({ id }) => id)
        .forEach((id) => mutateRemoveBox(draft, id));
      const next = draft.containers.findIndex((c) => c.id === nextId);
      const at = next === -1 ? draft.containers.length : next;
      draft.containers.splice(at, 0, ...anchors.map(({ container }) => container));
      anchors.forEach(({ columns, boxes }) => {
        draft.columns.push(...columns);
        draft.boxes.push(...boxes);
      });
    });
    delete draft.currentDocument;
  });
